/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component
} from '@angular/core';
import {
	UntypedFormControl
} from '@angular/forms';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	TransactionStatusEffectiveDateDirective
} from '@insurance/directives/transaction-status-effective-date.directive';
import {
	IInsuranceEntityTypes
} from '@insurance/interfaces/insurance-entity-types.interface';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	DateHelper
} from '@shared/helpers/date.helper';
import {
	ObjectArrayHelper
} from '@shared/helpers/object-array.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	EntityType
} from '@shared/implementations/entities/entity-type';
import {
	User
} from '@shared/implementations/users/user';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	IActionResponse
} from '@shared/interfaces/workflow/action-response.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	SessionService
} from '@shared/services/session.service';
import {
	DateTime
} from 'luxon';
import {
	MenuItem
} from 'primeng/api';

@Component({
	selector: 'transaction-cancel-effective-date',
	templateUrl: './transaction-cancel-effective-date.component.html',
	styleUrls: [
		'./transaction-cancel-effective-date.component.scss'
	]
})

/**
 * A component representing a wizard step for setting the effective date
 * of a policy transaction status change.
 *
 * @export
 * @class TransactionCancelEffectiveDateComponent
 * @extends {TransactionCancelEffectiveDateComponent}
 */
export class TransactionCancelEffectiveDateComponent
	extends TransactionStatusEffectiveDateDirective
{
	/**
	 * Initializes an instance of the transaction status effective date
	 * component.
	 *
	 * @param {Router} router
	 * The router used for navigation.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used in this component.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service used in this component.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public insuranceService: InsuranceService,
		public entityTypeApiService: EntityTypeApiService,
		public entityInstanceApiService: EntityInstanceApiService,
		public sessionService: SessionService)
	{
		super(
			insuranceService,
			entityTypeApiService,
			entityInstanceApiService);
	}

	/**
	 * Gets the workflow action being executed against the current
	 * wizard step entity instance.
	 *
	 * @type {string}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public static readonly workflowActionName: string =
		'TransactionCancel';

	/**
	 * Gets or sets the newly created pending cancel transaction id.
	 *
	 * @type {number}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public createdPendingCancelTransactionId: number;

	/**
	 * Gets or sets the existing transaction instance that is being cancelled.
	 *
	 * @type {IEntityInstance}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public existingTransactionInstance: IEntityInstance;

	/**
	 * Gets or sets the set of insurance entity types.
	 *
	 * @type {IInsuranceEntityTypes}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public insuranceEntityTypes: IInsuranceEntityTypes;

	/**
	 * Gets or sets a value that indicates if new navigation data should be set.
	 *
	 * @type {boolean}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public override updateNavigationData: boolean = false;

	/**
	 * Gets or sets the message that will be set if the valid wizard step check
	 * returns false.
	 *
	 * @type {string}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public invalidWizardStepMessage: string = AppConstants.empty;

	/**
	 * Gets or sets the formly layout used in implementing components. This
	 * value can be overridden in the implementing component.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public override staticFormlyLayout: FormlyFieldConfig[] =
		<FormlyFieldConfig[]>
		[
			{
				key: 'data.statusEffectiveDate',
				type: FormlyConstants.customControls.customCalendar,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					label: 'Effective Date',
					required: true,
					quickDateSelection:
						<MenuItem>
						{
							label: 'Override',
							tooltipOptions: {
								tooltipLabel: AppConstants.empty
							},
							command:
								() =>
								{
									const effectiveDate: DateTime =
										this.overrideButtonClick();

									// The effective date is only set for
									// underwriting and non-payment reasons.
									if (AnyHelper.isNull(effectiveDate))
									{
										return null;
									}

									return DateHelper
										.fromUtcSystemIsoToSimulatedLocalDate(
											effectiveDate.toISO());
								}
						},
					change: (field: FormlyFieldConfig) =>
					{
						setTimeout(
							() =>
							{
								this.getEffectiveDateBasedTransaction();
								const confirmationTextDisplay:
									FormlyFieldConfig =
									field.parent.fieldGroup.find(
										(textDisplay: FormlyFieldConfig) =>
											textDisplay.key ===
												this.reversalConfirmation);

								confirmationTextDisplay.props
									.reloadTextDisplay();
							},
							AppConstants.time.oneHundredMilliseconds);
					}
				},
				validators: {
					validEffectiveDate: {
						expression:
							(control: UntypedFormControl) =>
								DateTime.fromISO(control.value)
									>= this.policyTermInceptionDate
										&& DateTime.fromISO(control.value)
											< this.policyTermExpirationDate,
						message: 'The date must be between the policy '
							+ 'effective and expiration dates.'
					}
				}
			},
			{
				key: 'data.flatCancel',
				type: FormlyConstants.customControls.customCheckbox,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					checkboxText: 'Flat Cancel',
					default: false,
				},
				expressions: {
					hide:
						(field: FormlyFieldConfig) =>
						{
							const effectiveDateField: FormlyFieldConfig =
								field.parent.fieldGroup
									.find(item =>
										(<string>item.key)
											.indexOf('EffectiveDate') > 0);

							const hidden: boolean =
								effectiveDateField.formControl.status
									=== AppConstants.formControlStatus.invalid
									|| field.model.data.statusEffectiveDate
											!== field.model.data.inceptionDate;

							if (hidden === true)
							{
								field.formControl.setValue(false);
							}

							return hidden;
						}
				}
			},
			{
				key: 'transactionReversalConfirmation',
				type: FormlyConstants.customControls.customTextDisplay,
				props: {
					title: 'Confirm Transaction Removal',
					useMarkdown: true,
					usePanelDisplay: true,
					centerText: false
				},
				expressions: {
					hide:
						(field: FormlyFieldConfig) =>
							this.hideTransactionReversalControls(field),
					'props.content':
						(field: FormlyFieldConfig) =>
							this.getTransactionReversalConfirmationContent(
								field)
				}
			},
			{
				key: 'data.reverseAllLedgerTransactions',
				type: FormlyConstants.customControls.customInputSwitch,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					label: 'Include all adjustments, write-offs, and '
						+ 'fees (such as installment, late, etc.)',
					default: false
				},
				expressions: {
					hide:
						(field: FormlyFieldConfig) =>
							this.hideTransactionReversalControls(field)
				}
			},
			{
				key: 'data.acknowledgeOfTransactionRemoval',
				type: FormlyConstants.customControls.customInputSwitch,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					label: 'I acknowledge that this will remove all '
						+ 'endorsement transactions effective on or after the '
						+ 'cancellation date.',
					default: false
				},
				validators: {
					agreementConsentMustBeYes: {
						expression: (
							_control: UntypedFormControl,
							field: FormlyFieldConfig) =>
							field.formControl.value,
						message: 'You must acknowledge and agree to the '
							+ 'removal of endorsement transactions to proceed'
					}
				},
				expressions: {
					hide:
						(field: FormlyFieldConfig) =>
							this.hideTransactionReversalControls(field)
				}
			}
		];

	/**
	 * Gets or sets the transaction reversal confirmation string.
	 *
	 * @type {string}
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private readonly reversalConfirmation: string =
		'transactionReversalConfirmation';

	/**
	 * Gets the transaction reversal confirmation content.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration of this control.
	 * @returns {boolean}
	 * the dynamic content with the accurate verbiage to display.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public getTransactionReversalConfirmationContent(field: FormlyFieldConfig):
		string
	{
		const selectedDate: string =
			DateHelper.formatDate(
				DateTime.fromISO(
					field.model.data
						.statusEffectiveDate));

		return 'This cancellation will remove all '
			+ 'endorsement transactions effective on or '
			+ `after ${selectedDate}. The removed `
			+ 'transaction will have their ledger entries '
			+ 'reversed and cannot be restored '
			+ 'automatically.';
	}

	/**
	 * Determines whether or not to hide the
	 * transaction reversal confirmation controls.
	 *
	 * @param {FormlyFieldConfig} field
	 * The formly field configuration of this control.
	 * @returns {boolean}
	 * The true or false hide value.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public hideTransactionReversalControls(field: FormlyFieldConfig): boolean
	{
		const hide: boolean =
			AnyHelper.isNull(this.existingTransactionInstance)
			|| !field.model.data.anyTransactionToReverse;

		if (hide === true)
		{
			field.formControl.setValue(false);
		}

		return hide;
	}

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public async performPostInitActions(): Promise<void>
	{
		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;
		this.insuranceEntityTypes =
			await this.insuranceService.populateInsuranceEntityTypes();

		this.context.source.addOrUpdateStepData(
			{
				statusEffectiveDate:
					currentData.isCalculatedEffectiveDate === true
						? await this.calculateEffectiveDate()
						: currentData.statusEffectiveDate,
				inceptionDate: this.policyTermInceptionDate.toISO()
			});

		await this.getEffectiveDateBasedTransaction();
		this.context.source.addToNext(this.createTransactionCancel.bind(this));
		this.setOverrideButtonStatus();

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Validates the wizard step based on the component logic to
	 * confirm if this should be displayed or not.
	 *
	 * @async
	 * @returns {Promise<boolean>}
	 * An awaitable promise that returns a value signifying whether or not
	 * the wizard step is valid for display.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public async isWizardStepValidForDisplay(): Promise<boolean>
	{
		return true;
	}

	/**
	 * Creates a cancellation transaction based on a selected transaction.
	 *
	 * @async
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	public async createTransactionCancel(): Promise<void>
	{
		await this.transactionCancel();

		this.navigateToPendingCancelTransaction();
	}

	/**
	 * Gets the calculated effective date based on the cancel reason settings.
	 *
	 * @async
	 * @returns {Promise<string>}
	 * An awaitable promise that returns the calculated date value based on the
	 * cancel reason settings as an ISO string.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private async calculateEffectiveDate(): Promise<string>
	{
		const currentData: any =
			this.context.source.activeMenuItem.currentData;

		const cancelReason: any =
			currentData.data.selectedReasons[0];

		const statusEffectiveDate: DateTime =
			cancelReason.cancelReasonType
				===	InsuranceConstants.cancelReasonTypes.underwriting
				|| cancelReason.cancelReasonType
					=== InsuranceConstants.cancelReasonTypes.nonPayment
				? await this.getEffectiveDateBasedOnSettings(cancelReason)
				: null;

		const isCalculatedEffectiveDate: boolean =
			!AnyHelper.isNull(statusEffectiveDate);

		if (isCalculatedEffectiveDate === true)
		{
			this.disableEffectiveDateControl();
		}
		else
		{
			this.hideOverrideButton();
		}

		this.context.source.addOrUpdateStepData(
			{
				isCalculatedEffectiveDate: isCalculatedEffectiveDate
			});

		return AnyHelper.isNull(statusEffectiveDate)
			? null
			: DateHelper.startOf(
				statusEffectiveDate)
				.toISO();
	}

	/**
	 * Sets the transactions effective date based on the reason settings.
	 *
	 * @async
	 * @param {any} cancelReason
	 * The cancel reason that contains the settings.
	 * @returns {Promise<DateTime>}
	 * An awaitable promise that  the calculated date value based on the cancel
	 * reason settings.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private async getEffectiveDateBasedOnSettings(
		cancelReason: any): Promise<DateTime>
	{
		const productInstance: IEntityInstance =
			await this.insuranceService.getProductByName(
				this.policyTerm.data.productName);

		const underwritingPeriod: number =
			productInstance.data.newBusiness.underwritingPeriod;

		const cancelEffectiveDate: DateTime =
			DateTime.fromISO(this.policyTerm.data.effectiveDate)
				.plus(
					{
						day: underwritingPeriod
					});

		const today: DateTime = DateTime.local();

		const cancelReasonsDays: number = today <= cancelEffectiveDate
			? cancelReason.cancelEffectiveDateWithinUnderwritingPeriod.days
			: cancelReason.cancelEffectiveDateOutsideUnderwritingPeriod.days;

		const effectiveDate: DateTime =
			DateTime.local()
				.plus(
					{
						day: cancelReasonsDays
					});

		return effectiveDate;
	}

	/**
	 * The override button click event.
	 *
	 * @returns {DateTime}
	 * Returns the previously selected effective date value.
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private overrideButtonClick(): DateTime
	{
		this.enableEffectiveDateControl();

		this.context.source.addOrUpdateStepData(
			{
				isCalculatedEffectiveDate: false
			});

		const currentData =
			this.context.source.activeMenuItem.currentData;

		const effectiveDate: DateTime =
			currentData.data.statusEffectiveDate === null
				? null
				: DateTime.fromISO(currentData.data.statusEffectiveDate);

		return effectiveDate;
	}

	/**
	 * Sets the override button status based on the user permissions.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private setOverrideButtonStatus(): void
	{
		const hasMembership: boolean =
			new User(this.sessionService.user)
				.hasMembership(
					[
						InsuranceConstants.securityGroups.underwriterManager
					]);

		if (hasMembership !== true)
		{
			this.disableOverrideButton();
		}
	}

	/**
	 * Disables the effective date control.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private disableEffectiveDateControl(): void
	{
		this.staticFormlyLayout[0]
			.props.disabled = true;
	}

	/**
	 * Enables the effective date control.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	 private enableEffectiveDateControl(): void
	 {
		 this.staticFormlyLayout[0]
			 .props.disabled = false;
	 }

	/**
	 * Disables the override button.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private disableOverrideButton(): void
	{
		this.staticFormlyLayout[0]
			.props.quickDateSelection.disabled = true;

		this.staticFormlyLayout[0]
			.props.quickDateSelection
			.tooltipOptions.tooltipLabel =
				'You don\'t have permission to override';
	}

	/**
	 * Hides the override button when a calculated effective date is not
	 * required.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private hideOverrideButton(): void
	{
		this.staticFormlyLayout[0]
			.props.quickDateSelection = null;
	}

	/**
	 * Executes the transaction cancel process by executing the
	 * TransactionCancel workflow action to create a cancellation transaction
	 * with the collected data from the Cancel Wizard.
	 *
	 * @async
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private async transactionCancel(): Promise<void>
	{
		setTimeout(
			() =>
			{
				this.context.source.wizardStepLoading = true;
			});

		await this.getEffectiveDateBasedTransaction();
		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;

		const queryString: string =
			this.entityInstanceApiService.formUrlParam(
				AppConstants.empty,
				{
					statusEffectiveDate:
						currentData.statusEffectiveDate,
					flatCancellation:
						currentData.flatCancel,
					isCalculatedEffectiveDate:
						currentData.isCalculatedEffectiveDate,
					parentId:
						currentData.id,
					comments:
						currentData.comments,
					reasons:
						ObjectArrayHelper.commaSeparatedPropertyValues(
							currentData.selectedReasons,
							AppConstants.commonProperties.id),
					reverseAllLedgerTransactions:
						currentData.reverseAllLedgerTransactions
				});

		this.existingTransactionInstance.data.effectiveDate =
			currentData.statusEffectiveDate;

		const entityType: IEntityType =
			await this.entityTypeApiService
				.getSingleQueryResult(
					`${AppConstants.commonProperties.name} eq `
						+ `'${this.existingTransactionInstance
							.entityType}'`,
					AppConstants.empty);
		const displayName: string =
			new EntityType(entityType)
				.displayName;

		const activityPromise:
			() => Promise<void> =
			async() =>
			{
				this.entityInstanceApiService.entityInstanceTypeGroup =
					entityType.group;
				const response: IActionResponse =
					await this.entityInstanceApiService
						.executeAction(
							this.existingTransactionInstance.id,
							TransactionCancelEffectiveDateComponent
								.workflowActionName,
							this.existingTransactionInstance,
							queryString);

				this.createdPendingCancelTransactionId =
					(<any>response).body.parameters[
						AppConstants.commonProperties.id];
			};

		return this.activityService.handleActivity(
			new Activity(
				activityPromise(),
				'<strong>Processing cancellation</strong>',
				'<strong>Cancellation processed</strong>',
				`${displayName} cancellation has been created.`,
				`${displayName} cancellation has not been created.`));
	}

	/**
	 * This will set the existing transaction instance based on the effective
	 * date.
	 *
	 * @async
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private async getEffectiveDateBasedTransaction(): Promise<void>
	{
		this.matchEffectiveDateToTransaction();
		const currentData: any =
			this.context.source.activeMenuItem.currentData.data;

		this.context.source.addOrUpdateStepData(
			{
				anyTransactionToReverse: this.anyTransactionToReverse(
					currentData.statusEffectiveDate)
			});

		this.entityInstanceApiService.entityInstanceTypeGroup =
			currentData.basedOnEntityTypeGroup;
		this.existingTransactionInstance =
			!AnyHelper.isNull(currentData.basedOnEntityId)
				? await this.entityInstanceApiService
					.get(currentData.basedOnEntityId)
				: null;
	}

	/**
	 * Gets if any transactions to reverse upon cancellation issuance.
	 * This will populate the source data for the reversal transaction
	 * acknowledgement hide/show controls.
	 *
	 * @param {string} currentEffectiveDate
	 * The current/selected effective date for cancellation.
	 * @returns {boolean}
	 * Whether any transactions will be reversed during cancellation.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private anyTransactionToReverse(currentEffectiveDate: string): boolean
	{
		return !AnyHelper.isNullOrWhitespace(currentEffectiveDate)
			&& this.transactionChildren
				.filter(
					(transaction: IEntityInstance) =>
						parseInt(
							DateHelper.getDateDifference(
								DateTime.fromISO(currentEffectiveDate),
								DateTime.fromISO(
									transaction.data.effectiveDate)),
							AppConstants.parseRadix) > 0
					&& transaction.data.status ===
						InsuranceConstants.transactionStatusTypes.issued
					&& transaction.data.type !==
						InsuranceConstants.transactionTypes.reversal)
				.length > 0;
	}

	/**
	 * This will navigate to the new cancel transaction using the transaction
	 * id provided.
	 *
	 * @memberof TransactionCancelEffectiveDateComponent
	 */
	private navigateToPendingCancelTransaction(): void
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${AppConstants.moduleNames.policy}/entities`,
				this.existingTransactionInstance.entityType,
				AppConstants.viewTypes.edit,
				this.createdPendingCancelTransactionId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									InsuranceConstants.layoutTypes.cancelSummary
							})
				}
			});
	}
}